import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { exportUrl, exportXlsxUrl, documentsLotUrl } from '../../services/api';
import { STATUTS, CATEGORIES_SCORE } from '../../constants/formOptions';
import Live from './Live';

const PRIMARY = '#002664'; // bleu du drapeau tchadien

const StatCard = ({ value, label, to, highlight }) => {
  const content = (
    <>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </>
  );
  const className = `stat-card ${highlight ? 'highlight' : ''}`;
  return to ? <Link to={to} className={className}>{content}</Link> : <div className={className}>{content}</div>;
};

const Statistics = () => {
  const { stats, lastUpdate } = useOutletContext();

  if (!stats) {
    return <div className="loader"><div className="spinner" /></div>;
  }

  const suivi = stats.suivi || {};
  const scoreStats = stats.par_score || {};
  const experience = stats.experience || {};

  const activite = (suivi.activite || []).map(d => ({
    jour: new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    count: d.count,
  }));

  const statutData = STATUTS
    .map(s => ({ name: s.label, value: stats.par_statut[s.value] || 0, color: s.color }))
    .filter(s => s.value > 0);

  const scoreData = CATEGORIES_SCORE
    .map(c => ({ name: c.label, value: scoreStats[c.value] || 0, color: c.color }))
    .filter(s => s.value > 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Tableau de bord</h1>
          <Live lastUpdate={lastUpdate} />
        </div>
        <div className="actions">
          <a className="btn btn-primary btn-sm" href={exportXlsxUrl({})}>Exporter tout (Excel)</a>
          <a className="btn btn-secondary btn-sm" href={exportUrl({})}>Exporter (CSV)</a>
          <a className="btn btn-secondary btn-sm" href={documentsLotUrl({})}>Tous les dossiers (ZIP)</a>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard value={stats.total} label="Candidatures" to="/recruteur/list" />
        <StatCard value={suivi.a_examiner || 0} label="À examiner" to="/recruteur/list?statut=soumise" highlight={suivi.a_examiner > 0} />
        <StatCard value={suivi.dernieres_24h || 0} label="Dernières 24 h" />
        <StatCard value={stats.par_statut.preselectionne || 0} label="Présélectionnées" to="/recruteur/list?statut=preselectionne" />
        <StatCard value={stats.par_statut.retenu || 0} label="Retenues" to="/recruteur/list?statut=retenu" />
        <StatCard value={experience.enquetes_sante || 0} label="Exp. santé / vaccination" />
        <StatCard value={stats.par_sexe.F || 0} label="Candidatures féminines" />
        <StatCard value={(scoreStats.score_moyen || 0).toFixed(0)} label="Score moyen / 100" />
      </div>

      <div className="charts-grid">
        <div className="chart-card wide">
          <h3>Candidatures reçues — 14 derniers jours</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={activite}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f2" />
              <XAxis dataKey="jour" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={30} />
              <Tooltip formatter={(v) => [v, 'Candidatures']} />
              <Bar dataKey="count" fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Statuts</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statutData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={statutData.length > 1 ? 2 : 0}>
                {statutData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Catégories de score</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={scoreData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={scoreData.length > 1 ? 2 : 0}>
                {scoreData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>
    </>
  );
};

export default Statistics;
